import { useState, useRef, useEffect, useCallback } from 'react';

interface VoiceMemoPlayerProps {
  fileUrl: string;
  durationSeconds: number;
  waveformData: number[] | null;
  transcript?: string | null;
  onPlaybackEnd?: () => void;
  autoPlay?: boolean;
}

const SPEEDS = [1, 1.5, 2] as const;

export function VoiceMemoPlayer({
  fileUrl,
  durationSeconds,
  waveformData,
  transcript,
  onPlaybackEnd,
  autoPlay,
}: VoiceMemoPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  const bars = waveformData ?? Array(64).fill(0.3);

  useEffect(() => {
    const audio = new Audio(fileUrl);
    audio.preload = 'metadata';
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => setLoaded(true));
    audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onPlaybackEnd?.();
    });

    if (autoPlay) {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, [fileUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [isPlaying]);

  const cycleSpeed = useCallback(() => {
    const nextIndex = (speedIndex + 1) % SPEEDS.length;
    setSpeedIndex(nextIndex);
    if (audioRef.current) {
      audioRef.current.playbackRate = SPEEDS[nextIndex];
    }
  }, [speedIndex]);

  const seekTo = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const dur = audio.duration || durationSeconds;
      audio.currentTime = pct * dur;
      setCurrentTime(audio.currentTime);
    },
    [durationSeconds],
  );

  const totalDuration = audioRef.current?.duration || durationSeconds;
  const progress = totalDuration > 0 ? currentTime / totalDuration : 0;

  return (
    <div style={{ maxWidth: 280 }}>
      <div className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-2 dark:bg-gray-800">
        {/* Play/Pause */}
        <button
          onClick={togglePlay}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700"
        >
          {isPlaying ? (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
              <rect x="2" y="1" width="4" height="12" rx="1" />
              <rect x="8" y="1" width="4" height="12" rx="1" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
              <polygon points="3,1 13,7 3,13" />
            </svg>
          )}
        </button>

        {/* Waveform */}
        <div
          className="flex flex-1 cursor-pointer items-end gap-px [--waveform-unplayed:#cbd5e1] dark:[--waveform-unplayed:#475569]"
          style={{ height: 32 }}
          onClick={seekTo}
        >
          {bars.map((amplitude, i) => {
            const pct = bars.length > 0 ? i / bars.length : 0;
            const isPlayed = pct < progress;
            const h = Math.max(4, amplitude * 28);
            return (
              <div
                key={i}
                className="flex-1 rounded-sm"
                style={{
                  height: h,
                  backgroundColor: isPlayed ? '#2563eb' : 'var(--waveform-unplayed)',
                  minWidth: 2,
                }}
              />
            );
          })}
        </div>

        {/* Time */}
        <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400" style={{ minWidth: 36 }}>
          {formatTime(isPlaying ? currentTime : durationSeconds)}
        </span>

        {/* Speed */}
        <button
          onClick={cycleSpeed}
          className="shrink-0 rounded px-1 text-xs font-medium text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          {SPEEDS[speedIndex]}x
        </button>
      </div>

      {/* Transcript */}
      {transcript && (
        <div className="mt-1 px-1">
          <button
            onClick={() => setShowTranscript((prev) => !prev)}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            {showTranscript ? 'Hide transcript' : 'Transcript'}
          </button>
          {showTranscript && (
            <div className="mt-1 rounded bg-gray-50 p-2 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {transcript}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
